import { useCallback } from 'react';

import { useEventsContext } from '#src/contexts/events/index.ts';
import { useGetProtoSport } from '#src/hooks/events/useGetProtoSport.ts';
import { useGetInteractionContext } from '#src/hooks/useGetInteractionContext.ts';
import { mapKeys } from '#src/lib/object.ts';
import { toSnakeCase } from '#src/lib/toSnakeCase.ts';
import { sportToJSON } from '#src/protos/events/shared/events.ts';

import type { WithOptionalCommonProperties } from './EventsType.ts';

type EventProperties = Record<
  string,
  number | string | string[] | boolean | null
>;

export const generateUseEvents = <
  EventTypes extends Record<string, EventProperties | void>,
>({
  defaultProperties,
}:
  | {
      defaultProperties?: EventProperties;
    }
  | undefined = {}) => {
  return () => {
    const { track: baseTrack } = useEventsContext();
    const getInteractionContext = useGetInteractionContext();

    const getSport = useGetProtoSport();

    const track = useCallback(
      <K extends keyof EventTypes & string>(
        event: K,
        ...rest: EventTypes[K] extends void
          ? []
          : [WithOptionalCommonProperties<EventTypes[K]>]
      ): void => {
        const snakeCasedProperties = rest[0]
          ? mapKeys(rest[0], v => {
              return toSnakeCase(v as string);
            })
          : {};
        baseTrack(event, {
          interaction_context: getInteractionContext(),
          sport: sportToJSON(getSport()),
          ...defaultProperties,
          ...snakeCasedProperties,
        });
      },
      [baseTrack, getSport, getInteractionContext]
    );
    return track;
  };
};
